import { useMemo } from "react";
import { useAuthContext } from "../../../lib/context/Auth/AuthContext";
import { transport } from "../grpcTransport";
import {
  ListPermissionGroupsRequest,
  CreatePermissionGroupRequest,
  UpdatePermissionGroupRequest,
  DeletePermissionGroupRequest,
  ListPermissionGroupMembersRequest,
  GetPermissionGroupRequest,
  ListPermissionGroupCandidatesRequest,
} from "./permissions";
import { PermissionsClient } from "./permissions.client";

export type CreateGroupRequest = Omit<
  CreatePermissionGroupRequest,
  "customerID"
>;
export type UpdateGroupRequest = Omit<
  UpdatePermissionGroupRequest,
  "customerId"
>;
export type DeleteGroupRequest = Omit<
  DeletePermissionGroupRequest,
  "customerID"
>;
export type ListGroupMembersRequest = Omit<
  ListPermissionGroupMembersRequest,
  "customerID"
>;
export type ListGroupCandidatesRequest = Omit<
  ListPermissionGroupCandidatesRequest,
  "customerID"
>;
export type GetGroupRequest = Omit<GetPermissionGroupRequest, "customerID">;

export const useGroupsRequests = () => {
  const { user, tokenInterceptor } = useAuthContext();

  const permissionsClient = useMemo(() => new PermissionsClient(transport), []);

  const listPermissionGroups = async () => {
    if (!user) {
      throw new Error("User not found!");
    }

    return permissionsClient.listPermissionGroups(
      {
        customerID: user.customerid,
      },
      {
        interceptors: [tokenInterceptor],
      }
    );
  };

  const listPermissionGroupMembers = async ({
    id,
    page,
    pageSize,
    search,
  }: ListGroupMembersRequest) => {
    if (!user) {
      throw new Error("User not found!");
    }

    return permissionsClient.listPermissionGroupMembers(
      {
        customerID: user.customerid,
        id,
        page,
        pageSize,
        search,
      },
      {
        interceptors: [tokenInterceptor],
      }
    );
  };

  const listPermissionGroupCandidates = async ({
    id,
    page,
    pageSize,
    search,
  }: ListGroupCandidatesRequest) => {
    if (!user) {
      throw new Error("User not found!");
    }

    return permissionsClient.listPermissionGroupCandidates(
      {
        customerID: user.customerid,
        id,
        page,
        pageSize,
        search,
      },
      {
        interceptors: [tokenInterceptor],
      }
    );
  };

  const getPermissionGroup = async ({ id }: GetGroupRequest) => {
    if (!user) {
      throw new Error("User not found!");
    }

    return permissionsClient.getPermissionGroup(
      {
        id,
        customerID: user.customerid,
      },
      {
        interceptors: [tokenInterceptor],
      }
    );
  };

  const createPermissionGroup = async ({
    name,
    description,
    accessEverything,
    rooms,
    desks,
    locations,
    members,
    deskBookingEnabled,
    roomBookingEnabled,
  }: CreateGroupRequest) => {
    if (!user) {
      throw new Error("User not found!");
    }

    return permissionsClient.createPermissionGroup(
      {
        customerID: user.customerid,
        name,
        description,
        accessEverything,
        rooms,
        desks,
        locations,
        members,
        deskBookingEnabled,
        roomBookingEnabled,
      },
      {
        interceptors: [tokenInterceptor],
      }
    );
  };

  const updatePermissionGroup = async ({
    id,
    name,
    description,
    accessEverything,
    rooms,
    desks,
    locations,
    deleteMembers,
    addMembers,
    deskBookingEnabled,
    roomBookingEnabled,
  }: UpdateGroupRequest) => {
    if (!user) {
      throw new Error("User not found!");
    }

    return permissionsClient.updatePermissionGroup(
      {
        id,
        customerId: user.customerid,
        name,
        description,
        accessEverything,
        rooms,
        desks,
        locations,
        deleteMembers,
        addMembers,
        deskBookingEnabled,
        roomBookingEnabled,
      },
      {
        interceptors: [tokenInterceptor],
      }
    );
  };

  const deletePermissionGroup = async ({ id }: DeleteGroupRequest) => {
    if (!user) {
      throw new Error("User not found!");
    }

    return permissionsClient.deletePermissionGroup(
      {
        id,
        customerID: user.customerid,
      },
      {
        interceptors: [tokenInterceptor],
      }
    );
  };

  return {
    listPermissionGroups,
    createPermissionGroup,
    updatePermissionGroup,
    deletePermissionGroup,
    listPermissionGroupMembers,
    listPermissionGroupCandidates,
    getPermissionGroup,
  };
};
